import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeftRight, Search, Building2, Users, UserPlus, Check, X, Clock,
  Send, PawPrint, DollarSign, Mail, Phone, MapPin, History, Bell,
  ChevronRight, ExternalLink, AlertCircle
} from 'lucide-react';

const TransferSystem = ({ user, animal, onClose, onTransferComplete }) => {
  const [activeTab, setActiveTab] = useState('transfer');
  const [transferType, setTransferType] = useState('rescue');
  const [connectedRescues, setConnectedRescues] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedRescue, setSelectedRescue] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showNewRescueForm, setShowNewRescueForm] = useState(false);
  const [transferHistory, setTransferHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  // New rescue form
  const [newRescue, setNewRescue] = useState({
    name: '', organization_name: '', email: '', phone: '', address: ''
  });

  // Adoptee form
  const [adoptee, setAdoptee] = useState({
    name: '', email: '', phone: '', address: ''
  });

  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchConnectedRescues();
    if (animal) {
      fetchTransferHistory();
    }
  }, [animal]);

  const fetchConnectedRescues = async () => {
    try {
      const response = await axios.get('/shelter/connected-rescues');
      setConnectedRescues(response.data || []);
    } catch (error) {
      console.error('Failed to fetch connected rescues');
    }
  };

  const fetchTransferHistory = async () => {
    if (!animal?.animal_id) return;
    try {
      const response = await axios.get(`/animals/${animal.animal_id}/transfer-history`);
      setTransferHistory(response.data?.transfer_history || []);
    } catch (error) {
      console.error('Failed to fetch transfer history');
    }
  };

  const searchRescues = async () => {
    if (!searchQuery || searchQuery.length < 2) return;
    setIsSearching(true);
    try {
      const response = await axios.get('/shelter/search-rescues', { params: { query: searchQuery } });
      setSearchResults(response.data?.results || []);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setIsSearching(false);
    }
  };

  const handleTransfer = async () => {
    if (!animal) return;
    setLoading(true);

    try {
      const transferData = {
        transfer_type: transferType,
        notes
      };

      if (transferType === 'rescue') {
        if (showNewRescueForm) {
          if (!newRescue.organization_name || !newRescue.email) {
            toast.error('Rescue name and email are required');
            setLoading(false);
            return;
          }
          transferData.new_rescue = newRescue;
        } else if (selectedRescue) {
          transferData.to_rescue_id = selectedRescue.user_id;
        } else {
          toast.error('Please select a rescue to transfer to');
          setLoading(false);
          return;
        }
      } else if (transferType === 'adoptee') {
        if (!adoptee.name || !adoptee.email) {
          toast.error('Adoptee name and email are required');
          setLoading(false);
          return;
        }
        transferData.adoptee = adoptee;
      }

      const response = await axios.post(`/shelter/animals/${animal.animal_id}/transfer`, transferData);
      
      if (transferType === 'adoptee') {
        toast.success('Transfer initiated! Payment link sent to adoptee.');
      } else {
        toast.success('Animal transferred successfully!');
      }
      
      onTransferComplete?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <ArrowLeftRight className="w-5 h-5" />
          Transfer {animal?.name}
        </DialogTitle>
      </DialogHeader>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="transfer">Transfer Pet</TabsTrigger>
          <TabsTrigger value="history">Transfer History</TabsTrigger>
        </TabsList>

        <TabsContent value="transfer" className="space-y-4 mt-4">
          {/* Animal Info */}
          <Card className="bg-stone-50">
            <CardContent className="pt-4">
              <div className="flex items-center gap-4">
                {animal?.photos?.[0] ? (
                  <img src={animal.photos[0].url || animal.photos[0]} alt="" className="w-16 h-16 rounded-lg object-cover" />
                ) : (
                  <div className="w-16 h-16 bg-stone-200 rounded-lg flex items-center justify-center">
                    <PawPrint className="w-8 h-8 text-stone-400" />
                  </div>
                )}
                <div>
                  <h3 className="font-semibold text-lg">{animal?.name}</h3>
                  <p className="text-sm text-stone-600">{animal?.species} • {animal?.breed}</p>
                  {animal?.microchip_number && (
                    <p className="text-xs text-stone-500 font-mono">Chip: {animal.microchip_number}</p>
                  )}
                </div>
                {animal?.original_rescue_name && (
                  <Badge variant="outline" className="ml-auto">
                    Original: {animal.original_rescue_name}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Transfer Type Selection */}
          <div className="space-y-3">
            <Label>Transfer To</Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setTransferType('rescue')}
                className={`p-4 border rounded-lg text-left transition ${
                  transferType === 'rescue' ? 'border-primary bg-primary/5' : 'hover:border-stone-400'
                }`}
              >
                <Building2 className={`w-6 h-6 mb-2 ${transferType === 'rescue' ? 'text-primary' : 'text-stone-400'}`} />
                <p className="font-medium">Another Rescue</p>
                <p className="text-xs text-stone-500">Transfer to partner rescue</p>
              </button>
              <button
                onClick={() => setTransferType('adoptee')}
                className={`p-4 border rounded-lg text-left transition ${
                  transferType === 'adoptee' ? 'border-primary bg-primary/5' : 'hover:border-stone-400'
                }`}
              >
                <UserPlus className={`w-6 h-6 mb-2 ${transferType === 'adoptee' ? 'text-primary' : 'text-stone-400'}`} />
                <p className="font-medium">New Owner (Adoptee)</p>
                <p className="text-xs text-stone-500">$20 lifetime fee applies</p>
              </button>
            </div>
          </div>

          {/* Rescue Transfer Form */}
          {transferType === 'rescue' && (
            <div className="space-y-4">
              {/* Connected Rescues */}
              {connectedRescues.length > 0 && !showNewRescueForm && (
                <div>
                  <Label>Connected Rescues</Label>
                  <div className="grid gap-2 mt-2 max-h-40 overflow-y-auto">
                    {connectedRescues.map((rescue) => (
                      <button
                        key={rescue.user_id}
                        onClick={() => setSelectedRescue(rescue)}
                        className={`flex items-center justify-between p-3 border rounded-lg transition ${
                          selectedRescue?.user_id === rescue.user_id ? 'border-primary bg-primary/5' : 'hover:border-stone-400'
                        }`}
                      >
                        <div className="text-left">
                          <p className="font-medium">{rescue.organization_name || rescue.name}</p>
                          <p className="text-xs text-stone-500">{rescue.city}, {rescue.state}</p>
                        </div>
                        {selectedRescue?.user_id === rescue.user_id && (
                          <Check className="w-5 h-5 text-primary" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Search Other Rescues */}
              {!showNewRescueForm && (
                <div>
                  <Label>Search Other Rescues</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      placeholder="Search by name, city, or email..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && searchRescues()}
                    />
                    <Button onClick={searchRescues} disabled={isSearching}>
                      <Search className="w-4 h-4" />
                    </Button>
                  </div>
                  {searchResults.length > 0 && (
                    <div className="mt-2 border rounded-lg max-h-40 overflow-y-auto">
                      {searchResults.map((rescue) => (
                        <button
                          key={rescue.user_id}
                          onClick={() => {
                            setSelectedRescue(rescue);
                            setSearchResults([]);
                          }}
                          className="w-full flex items-center justify-between p-3 hover:bg-stone-50 border-b last:border-b-0"
                        >
                          <div className="text-left">
                            <p className="font-medium">{rescue.organization_name || rescue.name}</p>
                            <p className="text-xs text-stone-500">{rescue.email}</p>
                          </div>
                          {rescue.is_connected ? (
                            <Badge variant="default" className="bg-green-100 text-green-800">Connected</Badge>
                          ) : (
                            <ChevronRight className="w-4 h-4 text-stone-400" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Add New Rescue */}
              <div className="pt-2 border-t">
                <Button
                  variant="link"
                  onClick={() => setShowNewRescueForm(!showNewRescueForm)}
                  className="text-primary p-0"
                >
                  {showNewRescueForm ? '← Back to search' : '+ Add rescue not in system'}
                </Button>
              </div>

              {showNewRescueForm && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">New Rescue Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Organization Name *</Label>
                        <Input
                          value={newRescue.organization_name}
                          onChange={(e) => setNewRescue({ ...newRescue, organization_name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Contact Name</Label>
                        <Input
                          value={newRescue.name}
                          onChange={(e) => setNewRescue({ ...newRescue, name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Email *</Label>
                        <Input
                          type="email"
                          value={newRescue.email}
                          onChange={(e) => setNewRescue({ ...newRescue, email: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Phone</Label>
                        <Input
                          value={newRescue.phone}
                          onChange={(e) => setNewRescue({ ...newRescue, phone: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Address</Label>
                      <Input
                        value={newRescue.address}
                        onChange={(e) => setNewRescue({ ...newRescue, address: e.target.value })}
                      />
                    </div>
                  </CardContent>
                </Card>
              )}

              {selectedRescue && !showNewRescueForm && (
                <Card className="border-primary bg-primary/5">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{selectedRescue.organization_name || selectedRescue.name}</p>
                        <p className="text-sm text-stone-600">{selectedRescue.email}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setSelectedRescue(null)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Adoptee Transfer Form */}
          {transferType === 'adoptee' && (
            <div className="space-y-4">
              <Card className="border-orange-200 bg-orange-50">
                <CardContent className="pt-4">
                  <div className="flex items-start gap-3">
                    <DollarSign className="w-5 h-5 text-orange-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-orange-800">$20 Lifetime Registration Fee</p>
                      <p className="text-sm text-orange-600">
                        The new owner will receive an email with a payment link.
                        Transfer completes only after payment.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>New Owner Name *</Label>
                  <Input
                    value={adoptee.name}
                    onChange={(e) => setAdoptee({ ...adoptee, name: e.target.value })}
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={adoptee.email}
                    onChange={(e) => setAdoptee({ ...adoptee, email: e.target.value })}
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={adoptee.phone}
                    onChange={(e) => setAdoptee({ ...adoptee, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <Label>Address</Label>
                  <Input
                    value={adoptee.address}
                    onChange={(e) => setAdoptee({ ...adoptee, address: e.target.value })}
                    placeholder="City, State"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Transfer Notes */}
          <div>
            <Label>Transfer Notes (Optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any notes about this transfer..."
              rows={2}
            />
          </div>

          {/* Important Notice */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-800">Transfer Information</p>
                  <ul className="text-blue-700 mt-1 space-y-1">
                    <li>• All files and medical records will be transferred</li>
                    <li>• Original rescue ({user?.organization_name || user?.name}) will remain on file permanently</li>
                    <li>• Transfer history cannot be removed</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <History className="w-5 h-5" />
                Transfer History
              </CardTitle>
              <CardDescription>
                This history is permanent and cannot be modified.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {animal?.original_rescue_name && (
                <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm font-medium text-green-800">
                    Original Rescue: {animal.original_rescue_name}
                  </p>
                  <p className="text-xs text-green-600">This rescue will always be on record</p>
                </div>
              )}
              
              {transferHistory.length > 0 ? (
                <div className="space-y-3">
                  {transferHistory.map((transfer, index) => (
                    <div key={transfer.transfer_id || index} className="flex items-start gap-3 p-3 border rounded-lg">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        transfer.transfer_type === 'adoption' ? 'bg-green-100' :
                        transfer.transfer_type === 'return_to_rescue' ? 'bg-orange-100' : 'bg-blue-100'
                      }`}>
                        <ArrowLeftRight className={`w-4 h-4 ${
                          transfer.transfer_type === 'adoption' ? 'text-green-600' :
                          transfer.transfer_type === 'return_to_rescue' ? 'text-orange-600' : 'text-blue-600'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-medium">
                            {transfer.from_rescue_name || transfer.from} → {transfer.to_rescue_name || transfer.to_adoptee_name || transfer.to}
                          </p>
                          <Badge variant="outline" className="text-xs">
                            {transfer.transfer_type === 'adoption' ? 'Adoption' :
                             transfer.transfer_type === 'return_to_rescue' ? 'Returned' : 'Transfer'}
                          </Badge>
                        </div>
                        <p className="text-xs text-stone-500">
                          {new Date(transfer.completed_at || transfer.initiated_at || transfer.date).toLocaleDateString()}
                        </p>
                        {transfer.notes && (
                          <p className="text-sm text-stone-600 mt-1">{transfer.notes}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-stone-500 py-4">No transfer history yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        {activeTab === 'transfer' && (
          <Button onClick={handleTransfer} disabled={loading} className="bg-primary hover:bg-primary/90">
            {loading ? 'Processing...' : (
              transferType === 'adoptee' ? 'Send Payment Link' : 'Transfer Animal'
            )}
          </Button>
        )}
      </DialogFooter>
    </DialogContent>
  );
};

export default TransferSystem;
